<template>
  <modal
    :loading="loading"
    :title="$t('set_label_sets.create_from_filters_title')"
    style="max-width: 1200px"
    @close_modal="close_modal"
  >
    <template v-slot:content>
      <v-text-field
        id="name"
        v-model="label_set_name"
        :label="$t('set_label_sets.edit_name')"
        required
      ></v-text-field>
      <v-text-field
        id="description"
        v-model="label_set_description"
        :label="$t('set_label_sets.edit_description')"
      ></v-text-field>
      
      <div class="row">
        <div class="col-md-3">
          <filter-tree
            ref="filterTree"
            :title="'Filter annotations:'"
            :initial_filter="initial_filter"
            :non_deletable_filter_types="[41]"
            :non_togglable_filter_types="[41]"
            @update="update"
            @load="load"
            :max_results="max_results"
          ></filter-tree>
        </div>
        <div class="col-md-9">
          <div>
            <grid
              v-if="is_grouped"
              @update_selection_asset_ids="update_selection"
              @reload="load"
              :items="grouped_images"
            ></grid>
            <grid
              v-else
              @update_selection_asset_ids="update_selection"
              @reload="load"
              :items="images"
            ></grid>
          </div>
        </div>
      </div>
    </template>
    <template v-slot:buttons>
      <v-btn
        color="primary"
        text
        id="cancel_button"
        @click.native="close_modal"
      >
        {{ $t('set_label_sets.btn_cancel') }}
      </v-btn>
      <v-btn
        color="primary"
        text
        id="create_button"
        :disabled="disable_create_button || !label_set_name || unique_labels.length === 0"
        @click.native="createLabelSet"
      >
        {{ $t('set_label_sets.btn_create_labelset') }}
      </v-btn>
    </template>
  </modal>
</template>

<script>
import Modal from "@/components/Modal";
import FilterTree from "@/components/asset_filter/FilterTree";
import Grid from "@/components/Grid";
import LoadLabelsMixin from "@/mixins/LoadLabelsMixin";

export default {
  name: "CreateLabelSetFromFiltersModal",
  mixins: [LoadLabelsMixin],
  components: {
    FilterTree,
    Grid,
    Modal,
  },
  data() {
    return {
      loading: false,
      disable_create_button: true,
      filter: false,
      unique_labels: [],
      label_set_name: "",
      label_set_description: "",
      grouped_results: [],
      images: [],
      grouped_images: [],
      max_results: 12,
      is_grouped: false,
      initial_filter: [
        { type: 27, value: "" },
        { type: 9, value: "Yes" },
        { type: 11, value: "No" },
        { type: 41, value: "Yes" }, // Always include groupedBy label
      ],
    };
  },
  methods: {
    close_modal() {
      this.$emit("close_modal");
    },
    update_selection() {
      // Not used but required by FilterAnnotationsWithPreview
    },
    loaded() {
      this.$set(this, "disable_create_button", false);
    },
    update_filter(filters) {
      this.$set(this, "filter", filters);
      this.$set(this, "disable_create_button", true);
      this.$set(this, "unique_labels", []);
    },
    update(filters) {
      // Ensure groupedBy label filter is always present
      if (filters) {
        // Remove any existing groupedBy filter first
        filters = filters.filter(f => f.type !== 41);
        // Add it back with correct value
        filters.push({ type: 41, value: "Yes" });
      }
      this.update_filter(filters);
    },
    async load(filters, limit) {
      this.update_filter(filters);
      if (!filters) {
        filters = this.filter;
        limit = limit || 12;
      }

      // Ensure groupedBy label filter is always present
      if (!filters.some((f) => f.type === 41 && f.value === "Yes")) {
        filters.push({ type: 41, value: "Yes" });
      }

      this.images = [];
      this.grouped_images = [];
      this.$set(this, "loading", true);

      try {
        let results = await this.$api.annotation.filter(filters, limit || 10000);
        this.$emit("loaded");
        let total_results = results.total;
        let items = results.items;
        let max_results = results.max_results;
        let is_grouped = results.is_grouped || false;

        this.$set(this, "max_results", max_results);
        this.$set(this, "is_grouped", is_grouped);

        if (items.length === 0) {
          this.$set(this, "max_results", 0);
          this.$set(this, "loading", false);
          await this.extractUniqueLabels();
          this.loaded();
          return;
        }

        if (is_grouped) {
          // Handle grouped results - load crop images for preview
          for (let i = 0; i < items.length; i++) {
            let item = items[i];
            
            let data = await this.$api.asset.get_crop(item.url, item.bbox, item.asset_id);
            let cropData = data.crops.map((crop) => {
              return {
                label: item.label,
                count: item.count,
                meta: item.meta,
                data: crop.crop,
                certainty: item.certainty,
                bbox_id: item.bbox_id,
                id: item.asset_id,
                annotation_type: item.annotation_type,
                is_grouped: true,
              };
            });
            this.grouped_images = this.grouped_images.concat(cropData);
          }
          this.$set(this, "loading", false);
          await this.extractUniqueLabels();
          this.loaded();
        } else {
          // Handle regular results - load crops
          for (let i = 0; i < items.length; i++) {
            let r = items[i];
            let url = r["url"];
            let label = r["label"];
            let asset_id = r["asset_id"];
            let bbox = r["bbox"];
            let bbox_id = r["bbox_id"];
            let certainty = r["certainty"];
            let meta = r["meta"];

            let data = await this.$api.asset.get_crop(url, bbox, asset_id);
            let cropData = data.crops.map((crop) => {
              return {
                asset_id: asset_id,
                meta: meta,
                data: crop.crop,
                certainty: certainty,
                bbox_id: bbox_id,
                label: label,
                id: asset_id,
              };
            });
            this.images = this.images.concat(cropData);

            if (this.images.length === total_results) {
              this.$set(this, "loading", false);
            }
          }
          await this.extractUniqueLabels();
          this.loaded();
        }
      } catch (error) {
        console.error("Error loading annotations:", error);
        this.$set(this, "loading", false);
      }
    },
    async extractUniqueLabels() {
      // Get the grouped results from the filter
      if (!this.filter) {
        return;
      }

      // Ensure groupedBy label filter is present
      let filters = JSON.parse(JSON.stringify(this.filter));
      const hasGroupedBy = filters.some(
        (f) => f.type === 41 && f.value === "Yes"
      );
      if (!hasGroupedBy) {
        filters.push({ type: 41, value: "Yes" });
      }

      try {
        // Get grouped results with a high limit to get all labels
        let results = await this.$api.annotation.filter(filters, 10000);
        
        if (results.is_grouped && results.items) {
          // Extract unique label names from grouped results
          const labelNames = results.items.map((item) => item.label);
          const uniqueLabelNames = [...new Set(labelNames)];
          this.$set(this, "unique_labels", uniqueLabelNames);
          this.$set(this, "grouped_results", results.items);
        } else if (results.items && results.items.length > 0) {
          // If not grouped, extract from regular results
          const labelNames = results.items.map((item) => item.label);
          const uniqueLabelNames = [...new Set(labelNames)];
          this.$set(this, "unique_labels", uniqueLabelNames);
        } else {
          this.$set(this, "unique_labels", []);
        }
      } catch (error) {
        console.error("Error extracting labels:", error);
        this.$set(this, "unique_labels", []);
      }
    },
    async createLabelSet() {
      if (!this.label_set_name || this.unique_labels.length === 0) {
        return;
      }

      this.$set(this, "loading", true);

      try {
        // Get all labels to match names to IDs
        await this.ensure_labels_loaded();
        const allLabels = this.labels;

        // Map label names to label IDs
        const labelIds = this.unique_labels
          .map((labelName) => {
            const label = allLabels.find((l) => l.name === labelName);
            return label ? label.id : null;
          })
          .filter((id) => id !== null);

        if (labelIds.length === 0) {
          await this.$store.dispatch("snackbars/add_error_snack", {
            message: "No valid labels found to add to labelset",
          });
          this.$set(this, "loading", false);
          return;
        }

        // Create the labelset
        const labelSet = await this.$api.label_set.addLabelSet(
          this.label_set_name,
          this.label_set_description
        );

        // Add labels to the labelset
        await this.$api.label_set.updateLabels(labelSet, labelIds);

        await this.$store.dispatch("snackbars/add_success_snack", {
          message: "Labelset created successfully",
        });

        this.$emit("reload");
        this.close_modal();
      } catch (error) {
        console.error("Error creating labelset:", error);
        await this.$store.dispatch("snackbars/add_error_snack", {
          message: error.message || "Failed to create labelset",
        });
      } finally {
        this.$set(this, "loading", false);
      }
    },
  },
  mounted() {
    // Ensure labels are loaded
    this.ensure_labels_loaded();
  },
};
</script>

<style scoped>
</style>

